import { useState, useCallback, useRef, useEffect } from 'react';
import { MediaCategory, Message, MessageWindowResponse } from '@/types/api';
import { getMessageWindow } from '@/api/messages';

interface UseTelegramMessagesOptions {
  /** Category filter; null = every message, attachment or not. */
  mediaType?: MediaCategory | null;
  /**
   * Message to centre the window on (a search jump). null = newest messages.
   * The anchor itself is included and lands at the bottom of the first page.
   */
  anchorId?: number | null;
  /**
   * Bumped by the caller on every jump. Without it, jumping to the message you
   * are already anchored on is a no-op — same anchor, same view, no reload —
   * and the panel stays wherever the reader had scrolled to.
   */
  anchorNonce?: number;
}

interface UseTelegramMessagesReturn {
  messages: Message[];
  isLoadingInitial: boolean;
  isLoadingOlder: boolean;
  isLoadingNewer: boolean;
  hasMoreOlder: boolean;
  hasMoreNewer: boolean;
  /** The first page failed. Distinct from "loaded, and the channel is empty". */
  loadFailed: boolean;
  reload: () => void;
  loadOlderMessages: () => void;
  loadNewerMessages: () => void;
}

const PAGE_SIZE = 50;
/** Higher than any real message_id: "before everything" = the newest page. */
const LATEST_ANCHOR = 999999999;

export const useTelegramMessages = (
  chatId: number | null,
  options: UseTelegramMessagesOptions = {}
) => {
  const { mediaType = null, anchorId = null, anchorNonce = 0 } = options;
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isLoadingNewer, setIsLoadingNewer] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [hasMoreNewer, setHasMoreNewer] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const loadingOlderRef = useRef(false);
  const loadingNewerRef = useRef(false);

  /**
   * Identity of the loaded view. It carries the filter and the anchor, not just
   * the chat: with only the chat id in here, changing the media filter on the
   * open chat hit the "already loaded" short-circuit and silently did nothing.
   */
  const viewKey = `${chatId ?? ''}|${mediaType ?? ''}|${anchorId ?? ''}|${anchorNonce}|${reloadCount}`;
  const currentViewRef = useRef<string | null>(null);
  /**
   * Generation of the loaded window, captured by every request at fetch time
   * and re-checked when it resolves. The viewKey cannot do this job: a view can
   * be LEFT AND RE-ENTERED (All → PDF → All) while its own earlier request is
   * still in flight, and that response would pass a viewKey comparison and
   * prepend a mid-channel page in front of the freshly fetched newest one —
   * a gap, duplicate ids, and a has_more that can kill paging outright.
   */
  const tokenRef = useRef(0);

  useEffect(() => {
    // Skip only when this exact view (chat + filter + anchor) is already loaded.
    if (chatId && currentViewRef.current === viewKey && messages.length > 0) return;

    // The window is being rebuilt. Bump the token so every in-flight response
    // becomes stale, and clear the paging flags here: their own `finally` is
    // token-guarded and must not clear the flags of the view that replaced it.
    // Between the two, the flags are always cleared whichever view answers.
    const token = ++tokenRef.current;
    loadingOlderRef.current = false;
    loadingNewerRef.current = false;
    setIsLoadingOlder(false);
    setIsLoadingNewer(false);

    if (!chatId) {
      currentViewRef.current = null;
      setMessages([]);
      setLoadFailed(false);
      setIsLoadingInitial(false);
      setHasMoreOlder(true);
      setHasMoreNewer(false);
      return;
    }

    currentViewRef.current = viewKey;
    setIsLoadingInitial(true);
    setLoadFailed(false);
    setMessages([]);
    setHasMoreOlder(true);
    setHasMoreNewer(anchorId !== null);

    getMessageWindow({
      chat_id: chatId,
      anchor_id: anchorId ?? LATEST_ANCHOR,
      direction: 'before',
      limit: PAGE_SIZE,
      include_anchor: anchorId !== null,
      media_type: mediaType ?? undefined,
    })
      .then((resp: MessageWindowResponse) => {
        if (tokenRef.current !== token) return; // stale
        setMessages(resp.messages);
        setHasMoreOlder(resp.has_more);
      })
      .catch((err) => {
        console.error('Failed to load messages:', err);
        if (tokenRef.current !== token) return;
        // An empty list and a failed request render identically otherwise.
        setLoadFailed(true);
      })
      .finally(() => {
        if (tokenRef.current === token) {
          setIsLoadingInitial(false);
        }
      });
  }, [viewKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Retry the first page after a failure — same view, fresh request. */
  const reload = useCallback(() => setReloadCount((n) => n + 1), []);

  const loadOlderMessages = useCallback(() => {
    if (!chatId || loadingOlderRef.current || !hasMoreOlder || messages.length === 0) return;

    const token = tokenRef.current;
    const oldestId = messages[0].message_id;
    loadingOlderRef.current = true;
    setIsLoadingOlder(true);

    getMessageWindow({
      chat_id: chatId,
      anchor_id: oldestId,
      direction: 'before',
      limit: PAGE_SIZE,
      media_type: mediaType ?? undefined,
    })
      .then((resp: MessageWindowResponse) => {
        if (tokenRef.current !== token) return;
        setMessages((prev) => [...resp.messages, ...prev]);
        setHasMoreOlder(resp.has_more);
      })
      .catch((err) => {
        console.error('Failed to load older messages:', err);
      })
      .finally(() => {
        // The rebuilt view cleared these already and may have a request of its
        // own running — only the owning view clears them here.
        if (tokenRef.current !== token) return;
        loadingOlderRef.current = false;
        setIsLoadingOlder(false);
      });
  }, [chatId, hasMoreOlder, messages, mediaType]);

  // Only reachable in anchored (jumped-to) views — the unanchored view already
  // starts at the newest message, so there is nothing newer to fetch.
  const loadNewerMessages = useCallback(() => {
    if (!chatId || loadingNewerRef.current || !hasMoreNewer || messages.length === 0) return;

    const token = tokenRef.current;
    const newestId = messages[messages.length - 1].message_id;
    loadingNewerRef.current = true;
    setIsLoadingNewer(true);

    getMessageWindow({
      chat_id: chatId,
      anchor_id: newestId,
      direction: 'after',
      limit: PAGE_SIZE,
      media_type: mediaType ?? undefined,
    })
      .then((resp: MessageWindowResponse) => {
        if (tokenRef.current !== token) return;
        setMessages((prev) => [...prev, ...resp.messages]);
        setHasMoreNewer(resp.has_more && resp.messages.length > 0);
      })
      .catch((err) => {
        console.error('Failed to load newer messages:', err);
      })
      .finally(() => {
        if (tokenRef.current !== token) return;
        loadingNewerRef.current = false;
        setIsLoadingNewer(false);
      });
  }, [chatId, hasMoreNewer, messages, mediaType]);

  return {
    messages,
    isLoadingInitial,
    isLoadingOlder,
    isLoadingNewer,
    hasMoreOlder,
    hasMoreNewer,
    loadFailed,
    reload,
    loadOlderMessages,
    loadNewerMessages,
  } as UseTelegramMessagesReturn;
};
